/**
 * useDashboard — fetches the operator landing metrics from GET /dashboard
 * (counts, category/status splits, pricing health, purchase spend,
 * customer sign-ups). One fetch on mount; reload() re-runs it.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES } from '../constants/routes.constants'
import { parseApiError } from '../utils/api/parseApiError'
import type { DashboardMetrics } from '../models/dashboard.model'

export type { DashboardMetrics } from '../models/dashboard.model'

interface DashboardBody {
  readonly success: true
  readonly data: { readonly metrics: DashboardMetrics }
}

export interface UseDashboardReturn {
  readonly metrics: DashboardMetrics | null
  readonly isLoading: boolean
  readonly error: string | null
  readonly reload: () => void
}

export function useDashboard(): UseDashboardReturn {
  const { authedFetch } = useAuth()
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    void (async () => {
      try {
        const res = await authedFetch(APIROUTES.DASHBOARD)
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as DashboardBody
        if (!cancelled) setMetrics(body.data.metrics)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load dashboard')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [authedFetch, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { metrics, isLoading, error, reload }
}
